import {
  AlertNotificationsSettingState,
  DraughtState,
  EEOIAERState,
  FormConfigurationState,
  FuelMonitoringMethodState,
  ManagementState,
  NormalisedConsumptionState,
  PrimeMoverState,
  ReportingOptionsState,
  ShipInformation,
  StaticInformationState,
  VesselModelState,
  VesselParticularsState,
  VesselSpec,
  VesselSpecVm,
} from '@navstation-portal/vessels/data-model/vessel';
import { MapType, getOptionFromMap } from './vessel-spec-vm-builder-helpers';

function buildDraughtVm(info: ShipInformation | undefined): DraughtState | undefined {
  if (!info) return undefined;

  const {
    ballast_draught,
    loaded_draught,
    min_ballast_draught_fwd,
    min_ballast_draught_aft,
    min_loaded_draught_aft,
    min_loaded_draught_fwd,
  } = info;

  return {
    ballastDraught: ballast_draught,
    loadedDraught: loaded_draught,
    minBallastDraughtFwd: min_ballast_draught_fwd,
    minBallastDraughtAft: min_ballast_draught_aft,
    minLoadedDraughtFwd: min_loaded_draught_fwd,
    minLoadedDraughtAft: min_loaded_draught_aft,
  };
}

function buildStaticInformation(vesselSpec: VesselSpec | null): StaticInformationState | undefined {
  const info = vesselSpec?.information;
  if (!info) return undefined;

  const {
    full_speed,
    slow_steaming,
    ae_power_unit,
    short_name,
    vessel_number,
    imo,
    call_sign,
    name,
    vessel_type,
    mail_address,
  } = info;

  const selectedVesselType = getOptionFromMap(vessel_type, MapType.vesselType);
  const selectedAePowerUnit = getOptionFromMap(ae_power_unit, MapType.aePowerUnit);

  return {
    vesselNumber: vessel_number,
    imoNumber: imo,
    callSign: call_sign,
    nameOfShip: name,
    vesselType: selectedVesselType,
    emailAddress: mail_address,
    vesLinkCode: short_name,
    veslinkSyncEnabled: vesselSpec?.veslink?.enabled ?? false,
    aePowerUnit: selectedAePowerUnit,
    fullSpeed: full_speed,
    slowSteaming: slow_steaming,
  };
}
function buildVesselModel(info: ShipInformation | undefined): VesselModelState | undefined {
  if (!info) return undefined;

  const {
    vessel_class,
    has_torque_meter,
    has_torsionmeter,
    manually_report_calculated_average_total_power,
    use_tcrpm_power,
    has_pmi_system,
    block_coefficient,
    transverse_area,
    anemomenter_height,
  } = info;

  return {
    vesselClass: vessel_class,
    hasTorqueMeter: has_torque_meter,
    hasTorsionMeter: has_torsionmeter,
    manuallyReportAvgTotalPower: manually_report_calculated_average_total_power,
    useTCRPMPower: use_tcrpm_power,
    hasPMISystem: has_pmi_system,
    blockCoefficient: block_coefficient,
    transverseArea: transverse_area,
    anemometerHeight: anemomenter_height,
  };
}

function buildFormConfiguration(vesselSpec: VesselSpec | null): FormConfigurationState | undefined {
  if (!vesselSpec?.information && !vesselSpec?.form.fields) return undefined;

  const {
    has_ig_generator,
    enforce_report_sequence,
    tank_based_reporting,
    has_scrubber,
    has_pto,
    has_pti,
    shaft_meter_located_before_pto_pti,
    dual_fuel,
  } = vesselSpec.information;

  const fields = vesselSpec.form.fields;

  const { tankPressureControl, hdgConsumptionReporting, scrRunningHoursReporting, totalMdgConsumption } = {
    tankPressureControl: fields['report.consumption.ab_tank_pressure_control']?.visible,
    hdgConsumptionReporting: fields['report.consumption.hdg_reporting']?.visible,
    scrRunningHoursReporting: fields['report.power.scr_running_hours_reporting']?.visible,
    totalMdgConsumption: fields['report.consumption.total_mdg_consumption']?.visible,
  };

  return {
    tankPressureControl,
    hdgConsumptionReporting,
    scrRunningHoursReporting,
    totalMdgConsumption,
    hasIGGenerator: has_ig_generator,
    enforceReportSequence: enforce_report_sequence,
    tankBasedReporting: tank_based_reporting,
    hasScrubber: has_scrubber,
    hasPTI: has_pti,
    hasPTO: has_pto,
    shaftMeterBeforePTOPTI: shaft_meter_located_before_pto_pti,
    dualFuel: dual_fuel,
  };
}

function buildEEOIAER(info: ShipInformation | undefined): EEOIAERState | undefined {
  if (!info) return undefined;

  const { eeoi_baseline_top, eeoi_baseline_bot, aer_baseline_top, aer_baseline_bot } = info;

  return {
    eeoiBaselineTop: eeoi_baseline_top,
    eeoiBaselineBot: eeoi_baseline_bot,
    aerBaselineTop: aer_baseline_top,
    aerBaselineBot: aer_baseline_bot,
  };
}

function buildVesselParticulars(info: ShipInformation | undefined): VesselParticularsState | undefined {
  if (!info) return undefined;
  const {
    dwt,
    dwt_reference_design,
    eedi,
    built,
    flag,
    teu,
    length,
    length_perpendiculars,
    displacement_at_scantling_draught,
    width,
    height_of_deck,
    gt,
    net_tonnage,
    cargo_capacity,
  } = info;

  return {
    dwt: dwt,
    dwtRefDesign: dwt_reference_design,
    eedi: eedi,
    built: built,
    flag: flag,
    teu: teu,
    length: length,
    lpp: length_perpendiculars,
    displacement: displacement_at_scantling_draught,
    width: width,
    height: height_of_deck,
    gt,
    netTonnage: net_tonnage,
    cargoCapacity: cargo_capacity,
  };
}

function buildManagement(info: ShipInformation | undefined): ManagementState | undefined {
  if (!info) return undefined;
  const {
    company_name,
    technical_operator,
    commercial_operator,
    pool,
    segment,
    classification_society,
    ice_class_notation,
  } = info;

  const iceClassOption = getOptionFromMap(ice_class_notation, MapType.iceClass);
  return {
    companyName: company_name,
    technicalOperator: technical_operator,
    commercialOperator: commercial_operator,
    pool,
    segment,
    classificationSociety: classification_society,
    iceClassNotation: iceClassOption,
  };
}

function buildPrimeMover(info: ShipInformation | undefined): PrimeMoverState | undefined {
  if (!info) return undefined;
  const { prime_mover_type } = info;
  const primeMoverType = getOptionFromMap(prime_mover_type, MapType.primeMover);
  return {
    primeMoversType: primeMoverType,
  };
}

function buildAlertNotificationSettings(info: ShipInformation | undefined): AlertNotificationsSettingState | undefined {
  if (!info) return undefined;
  const { minimum_safe_bunker_hsls, minimum_safe_bunker_uls, ti_xml_sync } = info;
  const tiXmlSyncEnabled = ti_xml_sync?.enabled;
  const tiXmlSyncEmails = ti_xml_sync?.emails.join(';');
  const tiXmlSyncInPort = ti_xml_sync?.in_port;
  return {
    minimumSafeBunkerHSLS: minimum_safe_bunker_hsls,
    minimumSafeBunkerULS: minimum_safe_bunker_uls,
    tiXmlSyncEnabled,
    tiXmlSyncEmails,
    tiXmlSyncInPort,
  };
}

function buildNormalisedConsumptionReference(
  info: ShipInformation | undefined
): NormalisedConsumptionState | undefined {
  if (!info) return undefined;
  const { normalized_consumption_reference, normalized_consumption_ballast_reference } = info;

  return {
    loadedNC: normalized_consumption_reference,
    ballastNC: normalized_consumption_ballast_reference,
  };
}

function buildFuelMonitoringMethod(info: ShipInformation | undefined): FuelMonitoringMethodState | undefined {
  if (!info) return undefined;
  const { mrv_method, imo_dcs_method } = info;
  const mrvOpt = getOptionFromMap(mrv_method, MapType.mrv);
  const imoOpt = getOptionFromMap(imo_dcs_method, MapType.dcs);
  return {
    mrvMethod: mrvOpt,
    imoDCS: imoOpt,
  };
}

function buildReportingOptions(info: VesselSpec | undefined): ReportingOptionsState {
  const fields = info?.form?.fields;

  return {
    isVesselReportingEnabled: fields?.['vessel_reporting_enabled']?.visible ?? true,
    isOffshoreReportingEnabled: fields?.['offshore_reporting_enabled']?.visible ?? true,
  };
}

export function buildVesselSpecVm(vesselSpec: VesselSpec): VesselSpecVm {
  return {
    staticInformation: buildStaticInformation(vesselSpec),
    vesselModel: buildVesselModel(vesselSpec.information),
    draught: buildDraughtVm(vesselSpec.information),
    formConfig: buildFormConfiguration(vesselSpec),
    eeoiaer: buildEEOIAER(vesselSpec?.information),
    vesselParticulars: buildVesselParticulars(vesselSpec?.information),
    management: buildManagement(vesselSpec?.information),
    primeMover: buildPrimeMover(vesselSpec?.information),
    alert: buildAlertNotificationSettings(vesselSpec?.information),
    nc: buildNormalisedConsumptionReference(vesselSpec?.information),
    fuelMonitoring: buildFuelMonitoringMethod(vesselSpec?.information),
    reportingOptions: buildReportingOptions(vesselSpec),
  };
}
